import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Sparkles, ArrowDown } from "lucide-react";
import { iconFor } from "@/lib/icons";

const CARD_BACK =
  "https://images.unsplash.com/photo-1755541608283-1f83b5be9e6e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHx2aW50YWdlJTIwdGFyb3QlMjBjYXJkJTIwYmFjayUyMGRlc2lnbnxlbnwwfHx8fDE3ODE5ODk5MDB8MA&ixlib=rb-4.1.0&q=85";

function FlipCard({ card, flipped, delay }) {
  const Icon = iconFor(card.icon);
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, delay: delay * 0.12 }}
      className="relative"
      style={{ perspective: 1000 }}
      data-testid="tarot-card"
    >
      <div
        className={`card-flip relative w-full aspect-[2/3] ${flipped ? "flipped" : ""}`}
        style={{ boxShadow: flipped ? "0 0 28px rgba(212,175,55,0.35)" : "0 8px 24px rgba(0,0,0,0.5)" }}
      >
        {/* Back */}
        <div className="card-face rounded-xl overflow-hidden gold-border">
          <img src={CARD_BACK} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#090514]/40 flex items-center justify-center">
            <Sparkles className="w-7 h-7 text-[#D4AF37]/70" />
          </div>
        </div>
        {/* Front */}
        <div className="card-face card-back rounded-xl overflow-hidden gold-border bg-gradient-to-b from-[#1a1031] to-[#0c0719] flex flex-col items-center justify-center p-3 text-center">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]/70 mb-2">{card.deck}</span>
          <Icon className="w-8 h-8 text-[#D4AF37] mb-3" />
          <span className="font-display text-base sm:text-lg leading-tight text-white">{card.name}</span>
          <span className="text-[11px] text-white/55 mt-1.5 leading-snug">{card.keyword}</span>
        </div>
      </div>
    </motion.div>
  );
}

export default function CardDraw({ cards, name, onReveal }) {
  const [flippedCount, setFlippedCount] = useState(0);

  useEffect(() => {
    setFlippedCount(0);
    const timers = cards.map((_, i) =>
      setTimeout(() => setFlippedCount((c) => Math.max(c, i + 1)), 700 + i * 550)
    );
    return () => timers.forEach(clearTimeout);
  }, [cards]);

  const allFlipped = flippedCount >= cards.length;

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 py-20">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-12 max-w-2xl"
      >
        <p className="text-xs uppercase tracking-[0.28em] text-[#D4AF37] mb-4">Die Karten werden gezogen</p>
        <h2 className="font-display font-light italic text-4xl md:text-5xl text-glow-gold mb-4">
          {name}, deine Karten enthüllen sich
        </h2>
        <p className="text-white/60 leading-relaxed">
          Aus dem Zusammenspiel von Lenormand‑ und Tarotkarten formt sich das Bild der Person,
          die als Nächstes in dein Leben tritt.
        </p>
      </motion.div>

      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-3 sm:gap-4 w-full max-w-5xl">
        {cards.map((card, i) => (
          <FlipCard key={i} card={card} flipped={i < flippedCount} delay={i} />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: allFlipped ? 1 : 0 }}
        transition={{ duration: 0.6 }}
        className="mt-14 relative"
      >
        <div className="absolute inset-x-6 -inset-y-1 rounded-full bg-[#D4AF37]/25 blur-xl animate-pulse-glow pointer-events-none" />
        <button
          data-testid="reveal-analysis-button"
          onClick={onReveal}
          disabled={!allFlipped}
          className="relative inline-flex items-center gap-2 rounded-full border border-[#D4AF37] text-[#D4AF37] font-medium tracking-wide px-10 py-3.5 hover:bg-[#D4AF37] hover:text-[#090514] transition-all duration-300 disabled:opacity-40"
        >
          Deine Analyse enthüllen
          <ArrowDown className="w-4 h-4" />
        </button>
      </motion.div>
    </section>
  );
}
